import { AxiosResponse } from "axios";
import api from "../Interceptors/Interceptor";
import ApiResponse from "../Models/ApiResponse";
import { ResourceLocation } from "../Constants/resourceLocations";
import { BaseService } from "./BaseService";
import { IKnowledgeArticleSectionModel } from "./KnowledgeArticleSectionService";

/** What a client may set on an article — the body of POST and PUT (backend: CrudKnowledgeArticleModel). */
export interface IKnowledgeArticleCrudModel {
    title: string;
    /** Optional PrimeIcons class (e.g. `pi pi-book`). Empty = default book icon. */
    icon?: string;
    tags?: string;
    /** Owning customer. Only a multi-customer user may choose it (within their own tree); empty = own customer. */
    customerUuid?: string;
    /** Share the article (read-only) with every customer below `customerUuid`. Only kept by the backend when
     *  that customer actually has sub-customers. */
    global?: boolean;
    accessModuleCode?: string;
    featured?: boolean;
    featuredExpiresAt?: string;
    status: string;
    validFrom?: string;
    expiresAt?: string;
    active?: boolean;
}

export interface IArticleCategoryLink {
    resourceLinkUuid: string;
    categoryUuid: string;
    categoryName: string;
}

/** An article as the backend returns it (backend: GetKnowledgeArticleModel). */
export interface IKnowledgeArticleModel extends IKnowledgeArticleCrudModel {
    uuid: string;
    /** False for an article shared down from a customer above the viewer: they can read it, never edit it. */
    manageable?: boolean;
    /** When the article's content last changed: newest of the article itself and its sections. */
    updatedAt?: string;
    /** Who made that last change. Article list only, and only on an article the user may manage. */
    updatedByName?: string;
    /** Aggregate counter, only ever changed through registerView(). */
    viewCount?: number;
    /** Display names of the linked categories. */
    categoryNames?: string[];
    /** Whether the current user pinned this article. */
    pinned?: boolean;
    /** GET /{uuid} only: the linked categories, with the link uuid the editor needs to remove one. */
    categories?: IArticleCategoryLink[];
    /** GET /{uuid} only: every section of the article, ordered. */
    sections?: IKnowledgeArticleSectionModel[];
    /** GET /by-location only: `dialog` embeds the article; `link` navigates to it. */
    relationDisplayMode?: string;
    /** GET /by-location only: section uuids this help link should render. Empty/omitted means all. */
    relationSectionIdentifiers?: string[];
}

/** The part of a loaded article that goes back to the backend on save — never its read-only fields. */
export const toKnowledgeArticleCrudModel = (article: IKnowledgeArticleModel): IKnowledgeArticleCrudModel => ({
    title: article.title,
    icon: article.icon,
    tags: article.tags,
    customerUuid: article.customerUuid,
    global: article.global,
    accessModuleCode: article.accessModuleCode,
    featured: article.featured,
    featuredExpiresAt: article.featuredExpiresAt,
    status: article.status,
    validFrom: article.validFrom,
    expiresAt: article.expiresAt,
    active: article.active,
});

/** A user-managed place this article is linked to — see `GET /{uuid}/relations`. */
export interface IArticleRelation {
    resourceLinkUuid: string;
    location: string;
    locationIdentifier: string;
    locationName: string;
    displayMode?: string;
    /** Empty/omitted means every section is shown on the target. */
    sectionIdentifiers?: string[];
}

export interface IKnowledgeArticleEvent {
    uuid: string;
    knowledgeArticleId?: string;
    eventType?: string;
    contentTypeIcon?: string;
    contentTypeDescription?: string;
    payload?: Record<string, unknown>;
    actorName?: string;
    createdBy?: string;
    createdAt?: string;
}

export class KnowledgeArticleService extends BaseService<IKnowledgeArticleModel, IKnowledgeArticleCrudModel> {
    protected modelUrl(): string {
        return "knowledge-article";
    }

    /** The article with everything its page needs in one response: sections, categories and the pin state. */
    public async fetchItem(uuid: string): Promise<AxiosResponse<ApiResponse<IKnowledgeArticleModel>>> {
        return api.get(`v1/knowledge-article/${uuid}`);
    }

    public async fetchByLocation(
        location: ResourceLocation,
        locationIdentifier: string
    ): Promise<AxiosResponse<ApiResponse<IKnowledgeArticleModel[]>>> {
        return api.get("v1/knowledge-article/by-location", { params: { location, locationIdentifier } });
    }

    /** The articles linked to any of these places — e.g. a module's overview plus one item in it — each
     *  article once. */
    public async fetchByLocations(
        targets: { location: ResourceLocation; locationIdentifier: string }[]
    ): Promise<IKnowledgeArticleModel[]> {
        const responses = await Promise.all(targets.map((target) => this.fetchByLocation(target.location, target.locationIdentifier)));
        const articlesByUuid = new Map<string, IKnowledgeArticleModel>();
        responses
            .flatMap((response) => response.data?.data ?? [])
            .forEach((article) => {
                if (article.uuid && !articlesByUuid.has(article.uuid)) {
                    articlesByUuid.set(article.uuid, article);
                }
            });
        return Array.from(articlesByUuid.values());
    }

    public async fetchRelations(uuid: string): Promise<AxiosResponse<ApiResponse<IArticleRelation[]>>> {
        return api.get(`v1/knowledge-article/${uuid}/relations`);
    }

    public async togglePin(uuid: string): Promise<AxiosResponse<ApiResponse<boolean>>> {
        return api.post(`v1/knowledge-article/${uuid}/pin`);
    }

    public async link(
        uuid: string,
        location: ResourceLocation,
        locationIdentifier: string,
        displayMode?: string,
        sectionIdentifiers?: string[]
    ): Promise<AxiosResponse<ApiResponse<string>>> {
        return api.post(`v1/knowledge-article/${uuid}/link`, {
            location,
            locationIdentifier,
            displayMode,
            sectionIdentifiers,
        });
    }

    public async unlink(resourceLinkUuid: string): Promise<AxiosResponse<ApiResponse<void>>> {
        return api.delete(`v1/knowledge-article/link/${resourceLinkUuid}`);
    }

    /** The activity timeline — management information, empty for anyone who cannot edit the article. */
    public async fetchEvents(uuid: string): Promise<AxiosResponse<ApiResponse<IKnowledgeArticleEvent[]>>> {
        return api.get(`v1/knowledge-article/${uuid}/event`);
    }

    public async fetchTags(): Promise<AxiosResponse<ApiResponse<string[]>>> {
        return api.get("v1/knowledge-article/tags");
    }

    /** Whether an article can be created for this customer (empty = own customer): its media is stored in
     *  that customer's knowledge-base GCP project, so the customer needs one. Same rule the backend applies
     *  on create. */
    public async fetchCanCreate(customerUuid?: string): Promise<AxiosResponse<ApiResponse<boolean>>> {
        return api.get("v1/knowledge-article/can-create", { params: { customerUuid: customerUuid || undefined } });
    }

    /** Whether an article owned by this customer (empty = own customer) can be marked global, i.e. the
     *  customer has sub-customers to share it with. */
    public async fetchCanShareGlobally(customerUuid?: string): Promise<AxiosResponse<ApiResponse<boolean>>> {
        return api.get("v1/knowledge-article/can-share-globally", { params: { customerUuid: customerUuid || undefined } });
    }

    public async registerView(uuid: string): Promise<AxiosResponse<ApiResponse<void>>> {
        return api.post(`v1/knowledge-article/${uuid}/view`);
    }
}
